export type ClockDirection = 'down' | 'up';

export interface ClockModel {
  name: string;
  direction: ClockDirection;
  runsIn: readonly string[];
}

export interface ClockState {
  valueAtAnchor: number;
  anchorAt: number | null;
}

export const stoppedClock = (value: number): ClockState => ({
  valueAtAnchor: value,
  anchorAt: null,
});

export const isRunning = (clock: ClockState): boolean =>
  clock.anchorAt !== null;

const elapsedSeconds = (clock: ClockState, now: number): number =>
  clock.anchorAt === null ? 0 : Math.max(0, (now - clock.anchorAt) / 1000);

export const readClock = (
  clock: ClockState,
  direction: ClockDirection,
  now: number,
): number => {
  const elapsed = elapsedSeconds(clock, now);
  const value =
    direction === 'down'
      ? clock.valueAtAnchor - elapsed
      : clock.valueAtAnchor + elapsed;

  return Math.max(0, value);
};

export const startClock = (clock: ClockState, now: number): ClockState =>
  isRunning(clock)
    ? clock
    : { valueAtAnchor: clock.valueAtAnchor, anchorAt: now };

export const stopClock = (
  clock: ClockState,
  direction: ClockDirection,
  now: number,
): ClockState =>
  isRunning(clock) ? stoppedClock(readClock(clock, direction, now)) : clock;

export const setClock = (
  clock: ClockState,
  value: number,
  now: number,
): ClockState =>
  isRunning(clock)
    ? { valueAtAnchor: value, anchorAt: now }
    : stoppedClock(value);

export const anchorFromWire = (
  value: number,
  direction: ClockDirection,
  running: boolean,
  ageSeconds: number,
  now: number,
): ClockState => {
  if (!running) {
    return stoppedClock(value);
  }

  const age = Math.max(0, ageSeconds);
  const aged = direction === 'down' ? Math.max(0, value - age) : value + age;

  return { valueAtAnchor: aged, anchorAt: now };
};
